import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowRight, Check, Footprints } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useI18n } from '../i18n/I18nProvider';
import { submitNextStep } from '../lib/api';

const EMPTY = { name: '', email: '', phone: '', note: '', steps: [] };
const inputCls = 'mt-2 bg-[#faf6ea] border-[#e5dcc5] h-12 rounded-xl focus-visible:ring-[#2e5340]';

export default function NextSteps() {
  const { t, lang } = useI18n();
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const toggle = (id) => setForm((f) => ({ ...f, steps: f.steps.includes(id) ? f.steps.filter((s) => s !== id) : [...f.steps, id] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.steps.length) {
      toast({ title: t.nextSteps.requiredTitle, description: t.nextSteps.requiredText });
      return;
    }
    setLoading(true);
    try {
      await submitNextStep({ ...form, language: lang });
      setForm(EMPTY);
      toast({ title: t.nextSteps.okTitle, description: t.nextSteps.okText });
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="next-steps" data-testid="next-steps-section" className="relative py-24 lg:py-32 bg-[#f0e9d8]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14">
        <div>
          <span className="text-[#b88a2a] text-xs uppercase tracking-wider-xtra font-medium inline-flex items-center gap-2"><Footprints size={14} /> {t.nextSteps.eyebrow}</span>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#1f3a2a] mt-4 leading-[1.05]">{t.nextSteps.title_1}<br /><span className="italic">{t.nextSteps.title_2}</span></h2>
          <p className="mt-6 text-[#4a5a4e] text-lg leading-relaxed max-w-md">{t.nextSteps.sub}</p>
        </div>
        <form onSubmit={submit} data-testid="next-steps-form" className="bg-white border border-[#e5dcc5] rounded-3xl p-7 lg:p-9 shadow-[0_20px_50px_-25px_rgba(31,58,42,0.25)] space-y-5">
          <div>
            <Label className="text-[#1f3a2a]">{t.nextSteps.pick} <span className="text-[#b88a2a]">*</span></Label>
            <div className="mt-3 grid sm:grid-cols-2 gap-2">
              {t.nextSteps.options.map((o) => {
                const on = form.steps.includes(o.id);
                return (
                  <button type="button" key={o.id} onClick={() => toggle(o.id)} data-testid={`next-step-option-${o.id}`} aria-pressed={on}
                    className={`text-left rounded-xl border p-3 flex gap-3 transition-colors ${on ? 'bg-[#1f3a2a] border-[#1f3a2a] text-[#f5efe3]' : 'bg-[#faf6ea] border-[#e5dcc5] text-[#1f3a2a] hover:border-[#2e5340]'}`}>
                    <span className={`mt-0.5 w-5 h-5 rounded-md shrink-0 flex items-center justify-center border ${on ? 'bg-[#d4a63d] border-[#d4a63d] text-[#1f3a2a]' : 'border-[#c9bd9c]'}`}>{on && <Check size={13} strokeWidth={3} />}</span>
                    <span><span className="block text-sm font-medium">{o.title}</span><span className={`block text-xs mt-0.5 ${on ? 'text-[#f5efe3]/70' : 'text-[#8a7a4e]'}`}>{o.text}</span></span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[#1f3a2a]">{t.nextSteps.name} <span className="text-[#b88a2a]">*</span></Label>
              <Input data-testid="next-steps-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t.nextSteps.yourName} className={inputCls} />
            </div>
            <div>
              <Label className="text-[#1f3a2a]">{t.nextSteps.email} <span className="text-[#b88a2a]">*</span></Label>
              <Input data-testid="next-steps-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t.nextSteps.emailPh} className={inputCls} />
            </div>
          </div>
          <div>
            <Label className="text-[#1f3a2a]">{t.nextSteps.phone} <span className="text-[#8a7a4e]">{t.nextSteps.optional}</span></Label>
            <Input data-testid="next-steps-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t.nextSteps.optional} className={inputCls} />
          </div>
          <div>
            <Label className="text-[#1f3a2a]">{t.nextSteps.note}</Label>
            <Textarea data-testid="next-steps-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t.nextSteps.notePh} className="mt-2 bg-[#faf6ea] border-[#e5dcc5] rounded-xl focus-visible:ring-[#2e5340] min-h-[100px]" />
          </div>
          <Button type="submit" disabled={loading} data-testid="next-steps-submit-btn" className="w-full bg-[#d4a63d] hover:bg-[#b88a2a] text-[#1f3a2a] rounded-full h-12 inline-flex items-center justify-center gap-2 font-medium">
            {loading ? t.nextSteps.sending : t.nextSteps.submit} <ArrowRight size={18} />
          </Button>
        </form>
      </div>
    </section>
  );
}
